/**
 * Payment gateway configuration utilities
 */

/**
 * Check if PayPal is properly configured
 * @returns {boolean} True if PayPal client ID is available
 */
export const isPayPalConfigured = () => {
  return !!(process.env.REACT_APP_PAYPAL_CLIENT_ID && 
           process.env.REACT_APP_PAYPAL_CLIENT_ID.trim() !== '');
};

/**
 * Check if Razorpay is properly configured
 * @returns {boolean} True if Razorpay key ID is available
 */
export const isRazorpayConfigured = () => {
  return !!(process.env.REACT_APP_RAZORPAY_KEY_ID && 
           process.env.REACT_APP_RAZORPAY_KEY_ID.trim() !== '');
};

/**
 * Check if any payment gateway is configured
 * @returns {boolean} True if at least one payment gateway is configured
 */
export const isAnyPaymentGatewayConfigured = () => {
  return isPayPalConfigured() || isRazorpayConfigured();
};

/**
 * Check if all payment gateways are configured
 * @returns {boolean} True if both PayPal and Razorpay are configured
 */
export const areAllPaymentGatewaysConfigured = () => {
  return isPayPalConfigured() && isRazorpayConfigured();
};

/**
 * Get the current environment
 * @returns {string} The current environment (development, staging, production)
 */
export const getCurrentEnvironment = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  
  // Check for staging environment
  if (process.env.REACT_APP_ENV === 'staging' || 
      window.location.hostname.includes('staging')) {
    return 'staging';
  }
  
  // Default to production for non-development builds
  return 'production';
};

/**
 * Check if subscription management should be disabled
 * In development, always allow subscription management
 * In staging/production, only allow if payment gateways are configured
 * @returns {boolean} True if subscription management should be disabled
 */
export const isSubscriptionManagementDisabled = () => {
  const environment = getCurrentEnvironment();
  
  // Always allow in development
  if (environment === 'development') {
    return false;
  }
  
  // In staging/production, disable if payment gateways are not configured
  return !isAnyPaymentGatewayConfigured();
};

/**
 * Get the reason why subscription management is disabled
 * @returns {string} The reason for disabling subscription management
 */
export const getSubscriptionDisabledReason = () => {
  const environment = getCurrentEnvironment();
  
  if (environment === 'development') {
    return '';
  }
  
  if (!isPayPalConfigured() && !isRazorpayConfigured()) {
    return 'Payment gateways are not configured yet. Coming soon!';
  }
  
  if (!isPayPalConfigured()) {
    return 'PayPal configuration is pending. Coming soon!';
  }
  
  if (!isRazorpayConfigured()) {
    return 'Razorpay configuration is pending. Coming soon!';
  }
  
  return '';
};